import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header/Header';
import { searchPrices, checkAvailability, createBooking, getCities, getProperties, G2_CONFIG } from '../../services/g2TravelApi';
import './TestG2Travel.css';

const TestG2Travel = ({ onNavigate }) => {
  const navigate = useNavigate();
  
  // Search form state
  const [cityId, setCityId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [propertySearch, setPropertySearch] = useState('');
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [showPropertyDropdown, setShowPropertyDropdown] = useState(false);
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [nights, setNights] = useState(1);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [childAges, setChildAges] = useState([]);
  const [nationality, setNationality] = useState('GB');
  const [currency, setCurrency] = useState('GBP');
  
  // Results state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [priceResults, setPriceResults] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [availabilityResult, setAvailabilityResult] = useState(null);
  const [bookingResult, setBookingResult] = useState(null);
  
  // Static data
  const [cities, setCities] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loadingStatic, setLoadingStatic] = useState(false);
  
  // Booking form state
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [passengerData, setPassengerData] = useState({
    title: 'Mr',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  // Set default dates (7 days from today - many APIs require advance booking)
  useEffect(() => {
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    const weekPlusOne = new Date();
    weekPlusOne.setDate(weekPlusOne.getDate() + 8);
    
    setCheckIn(nextWeek.toISOString().split('T')[0]);
    setCheckOut(weekPlusOne.toISOString().split('T')[0]);
    setNights(1);
  }, []);

  // Load static data
  useEffect(() => {
    loadStaticData();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.test-g2-search-group')) {
        setShowCityDropdown(false);
        setShowPropertyDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadStaticData = async () => {
    setLoadingStatic(true);
    try {
      const [citiesData, propertiesData] = await Promise.all([
        getCities(),
        getProperties(),
      ]);
      console.log('Loaded cities:', citiesData?.length || 0);
      console.log('Loaded properties:', propertiesData?.length || 0);
      console.log('Sample city:', citiesData?.[0]);
      console.log('Sample property:', propertiesData?.[0]);
      setCities(Array.isArray(citiesData) ? citiesData : []);
      setProperties(Array.isArray(propertiesData) ? propertiesData : []);
    } catch (err) {
      console.error('Error loading static data:', err);
      setCities([]);
      setProperties([]);
    } finally {
      setLoadingStatic(false);
    }
  };

  // Update nights when dates change
  useEffect(() => {
    if (checkIn && checkOut) {
      const checkInDate = new Date(checkIn);
      const checkOutDate = new Date(checkOut);
      const diffTime = Math.abs(checkOutDate - checkInDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setNights(diffDays || 1);
    }
  }, [checkIn, checkOut]);

  // Update child ages array when children count changes
  useEffect(() => {
    if (children > 0) {
      const newAges = Array(children).fill(0).map((_, i) => childAges[i] || 5);
      setChildAges(newAges);
    } else {
      setChildAges([]);
    }
  }, [children]);

  // Filter cities and properties based on search
  const filteredCities = (() => {
    if (!citySearch) return [];
    const searchLower = citySearch.toLowerCase();

    const matches = cities.filter(city => {
      const cityName = (city.name || city.cityName || city.city || '').toLowerCase();
      const cityCode = (city.code || city.cityCode || city.id || city.cityId || '').toString().toLowerCase();
      return cityName.includes(searchLower) || cityCode.includes(searchLower);
    });

    // Deduplicate by city name (case-insensitive) to avoid repeated entries.
    // If name is missing, fall back to a code/id-based key.
    const seen = new Set();
    const unique = [];
    for (const city of matches) {
      const cityName = (city.name || city.cityName || city.city || '').toString().trim().toLowerCase();
      const cityCode = (city.code || city.cityCode || city.id || city.cityId || '').toString().trim().toLowerCase();
      const key = cityName || cityCode;
      if (!key) continue;
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(city);
    }
    return unique;
  })();

  const filteredProperties = properties.filter(property => {
    if (!propertySearch) return false;
    const searchLower = propertySearch.toLowerCase();
    const propName = (property.name || property.propertyName || property.hotelName || property.title || '').toLowerCase();
    const propCode = (property.code || property.propertyCode || property.id || property.propertyId || '').toLowerCase();
    return propName.includes(searchLower) || propCode.includes(searchLower);
  });

  const handleCitySelect = (city) => {
    const cityIdValue = city.id || city.cityId || '';
    const cityName = city.name || city.cityName || city.city || '';
    
    console.log('🔍 City object received:', city);
    console.log('🔍 Extracted cityIdValue:', cityIdValue, 'from fields:', { id: city.id, cityId: city.cityId });
    
    if (!cityIdValue || cityIdValue.trim() === '') {
      console.error('❌ ERROR: City ID is empty! City object:', city);
      alert('Error: Could not extract city ID. Please try selecting the city again.');
      return;
    }
    
    setSelectedCity(city);
    setCityId(cityIdValue);
    setCitySearch(cityName);
    setShowCityDropdown(false);
    
    // Clear property selection
    setSelectedProperty(null);
    setPropertyId('');
    setPropertySearch('');
    
    console.log('✅ City selected and ID set:', { name: cityName, id: cityIdValue, stateCityId: cityIdValue });
  };

  const handlePropertySelect = (property) => {
    const propertyName = property.name || property.propertyName || property.hotelName || property.title || '';
    const propertyIdValue = property.id || property.propertyId || '';
    
    setSelectedProperty(property);
    setPropertyId(propertyIdValue);
    setPropertySearch(propertyName);
    setShowPropertyDropdown(false);
    
    // Clear city selection
    setSelectedCity(null);
    setCityId('');
    setCitySearch('');
    
    console.log('✅ Property selected:', { name: propertyName, id: propertyIdValue });
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    // Get ID from selected object if state ID is empty (fallback)
    let finalCityId = cityId;
    let finalPropertyId = propertyId;
    
    // If cityId is empty but we have a selected city, use its ID
    if ((!finalCityId || finalCityId.trim() === '') && selectedCity) {
      finalCityId = selectedCity.id || selectedCity.cityId || '';
      setCityId(finalCityId); // Update state
      console.log('⚠️ City ID was empty, using ID from selectedCity:', finalCityId);
    }
    
    // If propertyId is empty but we have a selected property, use its ID
    if ((!finalPropertyId || finalPropertyId.trim() === '') && selectedProperty) {
      finalPropertyId = selectedProperty.id || selectedProperty.propertyId || '';
      setPropertyId(finalPropertyId); // Update state
      console.log('⚠️ Property ID was empty, using ID from selectedProperty:', finalPropertyId);
    }
    
    // Validate that we have a valid ID
    const validCityId = finalCityId && finalCityId.trim() !== '';
    const validPropertyId = finalPropertyId && finalPropertyId.trim() !== '';
    
    if (!validCityId && !validPropertyId) {
      setError('Please select either a city or property by name');
      return;
    }
    
    // Log what we're about to send
    console.log('🔍 Search validation:', {
      cityId: finalCityId || 'empty',
      propertyId: finalPropertyId || 'empty',
      cityIdLength: finalCityId?.length || 0,
      propertyIdLength: finalPropertyId?.length || 0,
      selectedCity: selectedCity ? { name: selectedCity.name || selectedCity.cityName, id: selectedCity.id || selectedCity.cityId } : 'none',
      selectedProperty: selectedProperty ? { name: selectedProperty.name || selectedProperty.propertyName, id: selectedProperty.id || selectedProperty.propertyId } : 'none'
    });

    if (!checkIn || !checkOut) {
      setError('Please select check-in and check-out dates');
      return;
    }

    setLoading(true);
    setError(null);
    setPriceResults(null);
    setSelectedOption(null);
    setAvailabilityResult(null);
    setBookingResult(null);
    setShowBookingForm(false);

    try {
      // Build roomPax array - API requires 'id' field for each room
      const roomPax = [{
        id: 1, // Room identifier (required by API)
        adults: adults,
        children: children,
        // Only include childAges if there are children
        ...(children > 0 && childAges.filter(age => age > 0).length > 0 ? {
          childAges: childAges.filter(age => age > 0)
        } : {})
      }];

      const searchParams = {
        distributionId: G2_CONFIG.distributionId,
        nationality: nationality,
        checkInDate: checkIn,
        nights: nights,
        currency: currency,
        roomPax: roomPax,
        includeRoomNames: true,
        includeRoomCapacity: true,
        includeResultsHavingNoCancelPolicy: true,
        includeMSP: true,
        includeSupplements: true,
        timeoutMillis: 120000,
      };

      // Use the validated IDs from above
      if (validPropertyId) {
        searchParams.propertyId = finalPropertyId.trim();
        console.log('🔍 Searching with Property ID:', searchParams.propertyId, '(length:', searchParams.propertyId.length, ')');
      } else if (validCityId) {
        searchParams.cityId = finalCityId.trim();
        console.log('🔍 Searching with City ID:', searchParams.cityId, '(length:', searchParams.cityId.length, ')');
      } else {
        throw new Error('No valid city or property ID provided');
      }

      console.log('📤 Search params:', JSON.stringify(searchParams, null, 2));

      const result = await searchPrices(searchParams);
      
      console.log('📥 Search result:', result);
      console.log('📥 Property options found:', result?.propertyOption?.length || 0);
      if (result?.propertyOption && result.propertyOption.length === 0) {
        console.log('⚠️  Empty results - Full response:', JSON.stringify(result, null, 2));
      }
      
      setPriceResults(result);
      
      if (result.error) {
        const errorMsg = result.error.description || result.error.code || 'Search failed';
        setError(`API Error: ${errorMsg}`);
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to search prices';
      setError(errorMsg);
      console.error('❌ Search error:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        error: err
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOption = async (option) => {
    setSelectedOption(option);
    setLoading(true);
    setError(null);
    setAvailabilityResult(null);
    setBookingResult(null);
    setShowBookingForm(false);

    try {
      const availabilityParams = {
        contextId: priceResults.contextId,
        bookingKey: priceResults.bookingKey,
        propertyId: option.propertyId,
        roomPax: [{
          adults: adults,
          children: children,
          childAges: childAges.filter(age => age > 0),
        }],
      };

      const result = await checkAvailability(availabilityParams);
      setAvailabilityResult(result);
      
      if (result.error) {
        setError(result.error.description || result.error.code || 'Availability check failed');
      } else {
        setShowBookingForm(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to check availability');
      console.error('Availability error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBooking = async (e) => {
    e.preventDefault();
    
    if (!passengerData.firstName || !passengerData.lastName || !passengerData.email) {
      setError('Please fill in all passenger details');
      return;
    }

    setLoading(true);
    setError(null);
    setBookingResult(null);

    try {
      const bookingParams = {
        contextId: priceResults.contextId,
        bookingKey: priceResults.bookingKey,
        propertyId: selectedOption.propertyId,
        roomPax: [{
          adults: adults,
          children: children,
          childAges: childAges.filter(age => age > 0),
        }],
        passenger: [{
          id: 1,
          title: passengerData.title,
          firstName: passengerData.firstName,
          lastName: passengerData.lastName,
          email: passengerData.email,
          phone: passengerData.phone || '',
          lead: true,
        }],
      };

      const result = await createBooking(bookingParams);
      setBookingResult(result);
      
      if (result.error) {
        setError(result.error.description || result.error.code || 'Booking failed');
      }
    } catch (err) {
      setError(err.message || 'Failed to create booking');
      console.error('Booking error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="test-g2-page">
      <Header onLogoClick={() => onNavigate('home')} onNavigate={onNavigate} />
      <main className="test-g2-main">
        <div className="test-g2-container">
          <h1>🧪 G2 Travel API Test Page</h1>
          <p className="test-g2-subtitle">
            Test the G2 Travel API integration - Search → Availability → Booking
          </p>

          {/* API Credentials Info */}
          <div className="test-g2-credentials">
            <h3>API Configuration</h3>
            <div className="test-g2-cred-item">
              <strong>Organization:</strong> {G2_CONFIG.credentials.organisation}
            </div>
            <div className="test-g2-cred-item">
              <strong>Username:</strong> {G2_CONFIG.credentials.username}
            </div>
            <div className="test-g2-cred-item">
              <strong>Distribution ID:</strong> {G2_CONFIG.distributionId}
            </div>
            <div className="test-g2-cred-item">
              <strong>Base URL:</strong> {G2_CONFIG.baseUrl}
            </div>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="test-g2-form">
            <h2>Step 1: Price Search</h2>
            
            <div className="test-g2-form-row">
              <div className="test-g2-form-group test-g2-search-group">
                <label>Search City (optional):</label>
                {loadingStatic && (
                  <div className="test-g2-loading-text">Loading cities...</div>
                )}
                <div className="test-g2-search-wrapper">
                  <input
                    type="text"
                    value={citySearch}
                    onChange={(e) => {
                      setCitySearch(e.target.value);
                      setShowCityDropdown(true);
                      if (!e.target.value) {
                        setSelectedCity(null);
                        setCityId('');
                      }
                    }}
                    onFocus={() => {
                      // Clear property selection when user focuses on city field
                      if (propertyId || selectedProperty) {
                        setSelectedProperty(null);
                        setPropertyId('');
                        setPropertySearch('');
                      }
                      if (citySearch || cities.length > 0) {
                        setShowCityDropdown(true);
                      }
                    }}
                    placeholder={loadingStatic ? "Loading cities..." : "Type city name (e.g., Paris, London)"}
                    disabled={loadingStatic}
                  />
                  {showCityDropdown && !loadingStatic && (
                    <>
                      {citySearch && filteredCities.length > 0 ? (
                        <div className="test-g2-dropdown">
                          {filteredCities.slice(0, 10).map((city, index) => (
                            <div
                              key={city.id || city.cityId || index}
                              className="test-g2-dropdown-item"
                              onClick={() => handleCitySelect(city)}
                            >
                              <strong>{city.name || city.cityName || city.city || 'Unknown'}</strong>
                              {(city.code || city.cityId || city.id) ? (
                                <span className="test-g2-dropdown-code">
                                  (ID: {city.id || city.cityId || city.code})
                                </span>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : citySearch && filteredCities.length === 0 && cities.length > 0 ? (
                        <div className="test-g2-dropdown">
                          <div className="test-g2-dropdown-item test-g2-no-results">
                            No cities found matching "{citySearch}"
                          </div>
                        </div>
                      ) : !citySearch && cities.length > 0 ? (
                        <div className="test-g2-dropdown">
                          <div className="test-g2-dropdown-item test-g2-hint">
                            Start typing to search cities...
                          </div>
                          <div className="test-g2-dropdown-item test-g2-hint">
                            {cities.length} cities available
                          </div>
                        </div>
                      ) : cities.length === 0 && !loadingStatic ? (
                        <div className="test-g2-dropdown">
                          <div className="test-g2-dropdown-item test-g2-no-results">
                            No cities loaded. Try entering a City ID manually or check the API connection.
                          </div>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
                {selectedCity && (
                  <div className="test-g2-selected">
                    Selected: <strong>{selectedCity.name || selectedCity.cityName}</strong>
                    <button
                      type="button"
                      className="test-g2-clear-btn"
                      onClick={() => {
                        setSelectedCity(null);
                        setCityId('');
                        setCitySearch('');
                      }}
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>

              <div className="test-g2-form-group test-g2-search-group">
                <label>Search Property/Hotel (optional):</label>
                {loadingStatic && (
                  <div className="test-g2-loading-text">Loading properties...</div>
                )}
                <div className="test-g2-search-wrapper">
                  <input
                    type="text"
                    value={selectedProperty ? (selectedProperty.name || selectedProperty.propertyName || selectedProperty.hotelName || selectedProperty.title || '') : propertySearch}
                    onChange={(e) => {
                      const value = e.target.value;
                      // If user starts typing, clear the selection to allow new search
                      if (selectedProperty && value !== (selectedProperty.name || selectedProperty.propertyName || selectedProperty.hotelName || selectedProperty.title || '')) {
                        setSelectedProperty(null);
                        setPropertyId('');
                      }
                      setPropertySearch(value);
                      setShowPropertyDropdown(true);
                    }}
                    onFocus={() => {
                      // Clear city selection when user focuses on property field
                      if (cityId || selectedCity) {
                        setSelectedCity(null);
                        setCityId('');
                        setCitySearch('');
                      }
                      // Show dropdown if no property is selected
                      if (!selectedProperty) {
                        setShowPropertyDropdown(true);
                      }
                    }}
                    placeholder={selectedProperty ? "Property selected below" : (loadingStatic ? "Loading properties..." : "Type hotel/property name or click to see options")}
                    disabled={loadingStatic}
                    readOnly={!!selectedProperty}
                    style={selectedProperty ? { backgroundColor: '#F0F9FF', cursor: 'not-allowed' } : {}}
                  />
                  {showPropertyDropdown && !loadingStatic && !selectedProperty && (
                    <>
                      {propertySearch && filteredProperties.length > 0 ? (
                        <div className="test-g2-dropdown">
                          {filteredProperties.slice(0, 10).map((property, index) => (
                            <div
                              key={property.id || property.propertyId || index}
                              className="test-g2-dropdown-item"
                              onClick={() => handlePropertySelect(property)}
                            >
                              <strong>{property.name || property.propertyName || property.hotelName || property.title || 'Unknown'}</strong>
                              {(property.id || property.propertyId) ? (
                                <span className="test-g2-dropdown-code">
                                  (ID: {property.id || property.propertyId})
                                </span>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : propertySearch && filteredProperties.length === 0 && properties.length > 0 ? (
                        <div className="test-g2-dropdown">
                          <div className="test-g2-dropdown-item test-g2-no-results">
                            No properties found matching "{propertySearch}"
                          </div>
                        </div>
                      ) : !propertySearch && properties.length > 0 ? (
                        <div className="test-g2-dropdown">
                          <div className="test-g2-dropdown-item test-g2-hint">
                            Start typing to search properties...
                          </div>
                          <div className="test-g2-dropdown-item test-g2-hint">
                            {properties.length} properties available
                          </div>
                        </div>
                      ) : properties.length === 0 && !loadingStatic ? (
                        <div className="test-g2-dropdown">
                          <div className="test-g2-dropdown-item test-g2-no-results">
                            No properties loaded from API. Please check the API connection.
                          </div>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
                {selectedProperty && (
                  <div className="test-g2-selected">
                    Selected: <strong>{selectedProperty.name || selectedProperty.propertyName || selectedProperty.hotelName || selectedProperty.title || 'Property'}</strong>
                    {selectedProperty.id || selectedProperty.propertyId ? (
                      <span style={{ marginLeft: '8px', fontSize: '0.875rem', color: '#6B7280' }}>
                        (ID: {selectedProperty.id || selectedProperty.propertyId})
                      </span>
                    ) : null}
                    <button
                      type="button"
                      className="test-g2-clear-btn"
                      onClick={() => {
                        setSelectedProperty(null);
                        setPropertyId('');
                        setPropertySearch('');
                        setShowPropertyDropdown(false);
                      }}
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="test-g2-form-row">
              <div className="test-g2-form-group">
                <label>Check-in Date:</label>
                <input
                  type="date"
                  value={checkIn}
                  onChange={(e) => setCheckIn(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="test-g2-form-group">
                <label>Check-out Date:</label>
                <input
                  type="date"
                  value={checkOut}
                  onChange={(e) => setCheckOut(e.target.value)}
                  min={checkIn || new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="test-g2-form-group">
                <label>Nights:</label>
                <input
                  type="number"
                  value={nights}
                  onChange={(e) => setNights(parseInt(e.target.value) || 1)}
                  min="1"
                  max="99"
                  required
                />
              </div>
            </div>

            <div className="test-g2-form-row">
              <div className="test-g2-form-group">
                <label>Adults:</label>
                <input
                  type="number"
                  value={adults}
                  onChange={(e) => setAdults(parseInt(e.target.value) || 1)}
                  min="1"
                  max="9"
                  required
                />
              </div>

              <div className="test-g2-form-group">
                <label>Children:</label>
                <input
                  type="number"
                  value={children}
                  onChange={(e) => setChildren(parseInt(e.target.value) || 0)}
                  min="0"
                  max="9"
                />
              </div>

              {children > 0 && (
                <div className="test-g2-form-group">
                  <label>Child Ages (comma-separated):</label>
                  <input
                    type="text"
                    value={childAges.join(',')}
                    onChange={(e) => {
                      const ages = e.target.value.split(',').map(a => parseInt(a.trim()) || 0);
                      setChildAges(ages);
                    }}
                    placeholder="e.g., 5, 8"
                  />
                </div>
              )}

              <div className="test-g2-form-group">
                <label>Nationality (ISO):</label>
                <input
                  type="text"
                  value={nationality}
                  onChange={(e) => setNationality(e.target.value.toUpperCase())}
                  placeholder="GB"
                  maxLength="2"
                  required
                />
              </div>

              <div className="test-g2-form-group">
                <label>Currency (ISO):</label>
                <input
                  type="text"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                  placeholder="GBP"
                  maxLength="3"
                  required
                />
              </div>
            </div>

            <button type="submit" disabled={loading} className="test-g2-button">
              {loading ? 'Searching...' : '🔍 Search Prices'}
            </button>
          </form>

          {error && (
            <div className="test-g2-error">
              <strong>Error:</strong> {error}
            </div>
          )}

          {loading && (
            <div className="test-g2-loading">
              <div className="test-g2-spinner"></div>
              <p>Processing request...</p>
            </div>
          )}

          {/* Price Results */}
          {priceResults && !priceResults.error && priceResults.propertyOption && (
            <div className="test-g2-results">
              <h2>✅ Price Search Results</h2>
              <div className="test-g2-info">
                <div><strong>Context ID:</strong> {priceResults.contextId}</div>
                <div><strong>Booking Key:</strong> {priceResults.bookingKey?.substring(0, 50)}...</div>
                {selectedCity && (
                  <div><strong>Searched City:</strong> {selectedCity.name} ({selectedCity.countryCode})</div>
                )}
                {selectedProperty && (
                  <div><strong>Searched Property:</strong> {selectedProperty.name || selectedProperty.propertyName}</div>
                )}
                <div><strong>Check-in:</strong> {checkIn} | <strong>Nights:</strong> {nights}</div>
              </div>
              
              {priceResults.propertyOption.length === 0 ? (
                <div className="test-g2-empty-results">
                  <h3>⚠️ No Properties Found</h3>
                  <p>The search completed successfully, but no properties are available for your criteria.</p>
                  <div className="test-g2-suggestions">
                    <h4>💡 Suggestions:</h4>
                    <ul>
                      <li><strong>Try a Property Search:</strong> Instead of searching by city, try searching for a specific hotel/property from the dropdown above.</li>
                      <li><strong>Try Different Dates:</strong> The dates you selected might not have availability. Try dates closer to today (within 30-90 days).</li>
                      <li><strong>Try a Different City:</strong> This city might not have properties available in the G2 system for these dates.</li>
                      <li><strong>Check Available Properties:</strong> Use the property dropdown to see which hotels are available in the system.</li>
                    </ul>
                    <div className="test-g2-tip">
                      <strong>💡 Tip:</strong> You have {properties.length} properties loaded. Try selecting one from the "Search Property/Hotel" field above and search again!
                    </div>
                  </div>
                </div>
              ) : (
                <div className="test-g2-options">
                  <p className="test-g2-found-count">Found {priceResults.propertyOption.length} property option(s):</p>
                  {priceResults.propertyOption.map((option, index) => (
                    <div key={index} className="test-g2-option-card">
                      <h3>{option.propertyName || `Property ${option.propertyId}`}</h3>
                      <div className="test-g2-option-details">
                        <div><strong>Property ID:</strong> {option.propertyId}</div>
                        <div><strong>Total Price:</strong> {option.totalPrice} {currency}</div>
                        {option.roomCode && <div><strong>Room Code:</strong> {option.roomCode}</div>}
                        {option.ratePlanCode && <div><strong>Rate Plan:</strong> {option.ratePlanCode}</div>}
                        {option.mealPlanCode && <div><strong>Meal Plan:</strong> {option.mealPlanCode}</div>}
                      </div>
                      <button
                        className="test-g2-select-btn"
                        onClick={() => handleSelectOption(option)}
                      >
                        Select & Check Availability
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Availability Result */}
          {availabilityResult && !availabilityResult.error && (
            <div className="test-g2-results">
              <h2>✅ Availability Confirmed</h2>
              <div className="test-g2-success">
                <p>Room is available! You can proceed with booking.</p>
              </div>
            </div>
          )}

          {/* Booking Form */}
          {showBookingForm && availabilityResult && !availabilityResult.error && (
            <form onSubmit={handleCreateBooking} className="test-g2-form">
              <h2>Step 3: Create Booking</h2>
              
              <div className="test-g2-form-row">
                <div className="test-g2-form-group">
                  <label>Title:</label>
                  <select
                    value={passengerData.title}
                    onChange={(e) => setPassengerData({ ...passengerData, title: e.target.value })}
                    required
                  >
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Ms">Ms</option>
                    <option value="Miss">Miss</option>
                  </select>
                </div>

                <div className="test-g2-form-group">
                  <label>First Name:</label>
                  <input
                    type="text"
                    value={passengerData.firstName}
                    onChange={(e) => setPassengerData({ ...passengerData, firstName: e.target.value })}
                    required
                  />
                </div>

                <div className="test-g2-form-group">
                  <label>Last Name:</label>
                  <input
                    type="text"
                    value={passengerData.lastName}
                    onChange={(e) => setPassengerData({ ...passengerData, lastName: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="test-g2-form-row">
                <div className="test-g2-form-group">
                  <label>Email:</label>
                  <input
                    type="email"
                    value={passengerData.email}
                    onChange={(e) => setPassengerData({ ...passengerData, email: e.target.value })}
                    required
                  />
                </div>

                <div className="test-g2-form-group">
                  <label>Phone:</label>
                  <input
                    type="tel"
                    value={passengerData.phone}
                    onChange={(e) => setPassengerData({ ...passengerData, phone: e.target.value })}
                  />
                </div>
              </div>

              <button type="submit" disabled={loading} className="test-g2-button">
                {loading ? 'Creating Booking...' : '📝 Create Booking'}
              </button>
            </form>
          )}

          {/* Booking Result */}
          {bookingResult && !bookingResult.error && (
            <div className="test-g2-results">
              <h2>✅ Booking Created Successfully!</h2>
              <div className="test-g2-success">
                <div><strong>Booking Reference:</strong> {bookingResult.bookingRef || 'N/A'}</div>
                <div><strong>Total Price:</strong> {bookingResult.totalPrice || selectedOption?.totalPrice} {currency}</div>
                {bookingResult.clientRef && <div><strong>Client Reference:</strong> {bookingResult.clientRef}</div>}
              </div>
              <div className="test-g2-warning">
                ⚠️ <strong>Important:</strong> Save the booking reference for future reference!
              </div>
            </div>
          )}

          {/* Raw Data Display */}
          {(priceResults || availabilityResult || bookingResult) && (
            <div className="test-g2-raw">
              <h2>📋 Raw API Response</h2>
              <pre className="test-g2-json">
                {JSON.stringify({ priceResults, availabilityResult, bookingResult }, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TestG2Travel;
